import { inject, Injectable } from '@angular/core';
import { Subscription, Subject } from 'rxjs';

import { LeaderboardHttpService } from '../http/http.service';
import { NonNullableFormBuilder } from '@angular/forms';
import { Pagination } from '@app/core/model/pagination.model';
import { UserSimpleGamification, UserSimpleGoal } from '../../model/user.model';
import { UserService } from '@app/user';

import { BroadcastService, BroadcastMessage } from '@app/core/service';

@Injectable()
export class LeaderboardLazyService {
    private readonly leaderboardHttpService = inject(LeaderboardHttpService);
    private readonly userService = inject(UserService);
    private readonly broadcastService = inject(BroadcastService);
    private readonly fb = inject(NonNullableFormBuilder);

    /**
     * Loading Flag
     */
    loading = false;

    /**
     * Leaderboard
     * {
        gamification: Record<string, Record<string, UserSimpleGamification[]>>;
        goal: Record<string, Record<string, UserSimpleGoal[]>>;
    }
     */
    _leaderboard: any = {
        gamification: {},
        goal: {},
    };

    /**
     * Total Items
     * {
        gamification: Record<string, number>;
        goal: Record<string, number>;
    }
     */
    _total: any = {
        gamification: {},
        goal: {},
    };

    /**
     * Total Items
     * {
        gamification: Record<string, number>;
        goal: Record<string, number>;
    }
     */
    _offsets: any = {
        gamification: {},
        goal: {},
    };

    /**
     * Ranges
     */
    readonly ranges = <const>['week', 'month', 'yeartodate', 'alltime'];

    /**
     * Types
     */
    types = <any>['goal'];

    /**
     * Range FormControl
     */
    readonly form: any = this.fb.group({
        type: this.fb.control<'goal' | 'gamification'>(this.types[0]),
        range: this.fb.control<LeaderboardLazyService['ranges'][number]>(this.ranges[0]),
    });

    /**
     * Pagination
     */
    _pagination: Pagination = {
        offset: 0,
        limit: 20,
    };

    /**
     * viewed Leaderboard
     */
    viewedPart: (UserSimpleGamification | UserSimpleGoal)[] | null = null;

    /**
     * Subject
     */
    private updatesEvent = new Subject();

    /**
     * Subscriptions
     */
    private subscriptions = new Subscription();

    init(): void {
        // gamification
        if (this.userService.access.includes('gamification')) {
            this.types.unshift('gamification');
        }
        this.initForm();
        this.subscriptions.add(this.broadcastService.broadcasts.subscribe((broadcast) => this.broadcasts(broadcast)));
    }

    destroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Subject
     */
    get updates() {
        return this.updatesEvent.asObservable();
    }

    /**
     * Get Leaderboard
     */
    get leaderboard() {
        // create empty tree
        this.createEmptyTree();
        // check if loaded
        if (this._leaderboard[this.form.getRawValue().type][this.form.getRawValue().range]['items']) {
            this.viewedPart = this._leaderboard[this.form.getRawValue().type][this.form.getRawValue().range]['items'];
        } else {
            this.loadList();
        }
        // return viewed part
        return this.viewedPart;
    }

    getLeaderBoard() {
        if (
            this._leaderboard[this.form.getRawValue().type][this.form.getRawValue().range]['items'].length <
            this._total[this.form.getRawValue().type][this.form.getRawValue().range]
        ) {
            this._offsets[this.form.getRawValue().type][this.form.getRawValue().range] =
                this._offsets[this.form.getRawValue().type][this.form.getRawValue().range] + 20;
            this.loadList();
        }
    }

    /**
     * Get Total
     */
    get total() {
        return this._total[this.form.getRawValue().type][this.form.getRawValue().range]
            ? this._total[this.form.getRawValue().type][this.form.getRawValue().range]
            : 0;
    }

    /**
     * Set Pagination
     */
    set pagination(pagination: Pagination) {
        this._pagination = pagination;
    }

    /**
     * Get Pagination
     */
    get pagination() {
        return this._pagination;
    }

    /**
     * Load Leaderboard
     */
    loadList(): void {
        if (this.loading) {
            return;
        }

        const options = this.form.value;

        this.loading = true;
        this.updatesEvent.next(true);

        switch (options.type) {
            case 'gamification':
                this.subscriptions.add(
                    this.leaderboardHttpService
                        .getLeaderboardGamification(options.range, this.pagination.limit, this._offsets[this.form.value.type][this.form.value.range])
                        .subscribe((data) => this.handleResponse(data.users, data.total, options.type, options.range)),
                );
                break;
            case 'goal':
                this.subscriptions.add(
                    this.leaderboardHttpService
                        .getLeaderboardGoal(options.range, this.pagination.limit, this._offsets[this.form.value.type][this.form.value.range])
                        .subscribe((data) => this.handleResponse(data.users, data.total, options.type, options.range)),
                );
                break;
        }
    }

    /**
     * Reset Pagination and Form
     */
    reset(): void {
        this.form.patchValue(
            {
                type: this.types[0],
                range: this.ranges[0],
            },
            { emitEvent: false },
        );
        this.pagination = {
            offset: 0,
            limit: 20,
        };
        this.viewedPart = null;
    }

    /**
     * Response Handler
     * @param users List of Users
     * @param total Total number of Users
     * @param type gamification or goal
     * @param range week, month or all
     * @param offset offset of the query
     */
    private handleResponse(users: (UserSimpleGamification | UserSimpleGoal)[], total: number, type: string, range: string): void {
        if (!this._leaderboard[type][range]['items']) {
            this._leaderboard[type][range]['items'] = users;
        } else {
            this._leaderboard[type][range]['items'] = [...this._leaderboard[type][range]['items'], ...users];
        }

        this._total[type][range] = total;
        this.loading = false;
        this.updatesEvent.next(true);
    }

    /**
     * Init Form
     */
    private initForm(): void {
        // subscription
        this.subscriptions.add(
            this.form.valueChanges.subscribe(() => {
                this.pagination = {
                    offset: 0,
                    limit: 20,
                };
                this.viewedPart = null;
                this.updatesEvent.next(true);
            }),
        );
    }

    /**
     * Create Empty tree
     */
    private createEmptyTree(): void {
        // type
        if (!this._leaderboard[this.form.value.type]) {
            this._leaderboard[this.form.value.type] = {};
            this._total[this.form.value.type] = {};
            this._offsets[this.form.value.type] = {};
        }
        // range
        if (!this._leaderboard[this.form.value.type][this.form.value.range]) {
            this._leaderboard[this.form.value.type][this.form.value.range] = {};
            this._total[this.form.value.type][this.form.value.range] = null;
            this._offsets[this.form.value.type][this.form.value.range] = 0;
        }
    }

    /**
     * Broadcast
     */
    private broadcasts(broadcast: BroadcastMessage): void {
        switch (broadcast.message) {
            case 'episode-completed':
            case 'discussion-topic-created':
            case 'discussion-topic-reply-created':
            case 'discussion-reply-reply-created':
            case 'user-self-edited':
                this.reset();
                break;
        }
    }
}
